
import { supabase } from './client';
import { DbCourse } from '@/types';

// Define the return type for profile course links
export interface ProfileCourseLink extends DbCourse {
  is_favorite: boolean;
  link_id: string;
}

// Get a user's favorite courses
export const getFavoriteCourses = async (userId: string): Promise<ProfileCourseLink[]> => {
  const { data, error } = await supabase
    .from('profile_course_link')
    .select(`
      id,
      is_favorite,
      course_id,
      courses:course_id (*)
    `)
    .eq('profile_id', userId)
    .eq('is_favorite', true);
  
  if (error) {
    console.error('Error fetching favorite courses:', error);
    return [];
  }
  
  // Map the returned data to extract just the course objects
  return data?.map(item => ({
    ...item.courses,
    is_favorite: true,
    link_id: item.id
  })) || [];
};

// Get all courses linked to a user
export const getUserCourses = async (userId: string): Promise<ProfileCourseLink[]> => {
  const { data, error } = await supabase
    .from('profile_course_link')
    .select(`
      id,
      is_favorite,
      course_id,
      courses:course_id (*)
    `)
    .eq('profile_id', userId);
  
  if (error) {
    console.error('Error fetching user courses:', error);
    return [];
  }
  
  // Map the returned data to extract course objects with additional properties
  return data?.map(item => ({
    ...item.courses,
    is_favorite: item.is_favorite,
    link_id: item.id
  })) || [];
};

// Add a course to user's list (with optional favorite flag)
export const addCourseToUser = async (userId: string, courseId: string, isFavorite: boolean = false) => {
  // First check if the link already exists
  const { data: existingLink } = await supabase
    .from('profile_course_link')
    .select('id')
    .eq('profile_id', userId)
    .eq('course_id', courseId)
    .single();
  
  if (existingLink) {
    // If link exists, update the favorite status
    return toggleFavoriteCourse(existingLink.id, isFavorite);
  }
  
  // Otherwise create a new link
  const { data, error } = await supabase
    .from('profile_course_link')
    .insert({
      profile_id: userId,
      course_id: courseId,
      is_favorite: isFavorite
    })
    .select()
    .single();
  
  if (error) {
    console.error('Error adding course to user:', error);
    return { error };
  }
  
  return { data };
};

// Toggle favorite status for a course
export const toggleFavoriteCourse = async (linkId: string, isFavorite: boolean) => {
  const { data, error } = await supabase
    .from('profile_course_link')
    .update({ is_favorite: isFavorite })
    .eq('id', linkId)
    .select()
    .single();
  
  if (error) {
    console.error('Error toggling favorite course:', error);
    return { error };
  }
  
  return { data };
};

// Remove a course from user's list
export const removeCourseFromUser = async (linkId: string) => {
  const { error } = await supabase
    .from('profile_course_link')
    .delete()
    .eq('id', linkId);

  if (error) {
    console.error('Error removing course from user:', error);
    return { error };
  }

  return { success: true };
};

// Set a user's home course
export const setHomeCourse = async (userId: string, courseId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ home_course_id: courseId })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error setting home course:', error);
    return { error };
  }

  return { data };
};

// Update profile fields (name, email)
export const updateProfile = async (
  userId: string,
  updates: { name?: string; email?: string }
) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating profile:', error);
    return { error };
  }

  return { data };
};
